import React, { useEffect, useMemo, useState } from "react";
import config from "./config";
import { useParams } from "react-router-dom";
import { SERVICE_HISTORY_PAGES } from "./serviceHistoryRoutes";
import { incidentPreview, incidentState, incidentLabel, incidentText, incidentReportLink } from "./serviceIncidentPresentation";
import "./ServiceStatusPage.css";

const API_URL = `${config.API_URL}api/User`;

const getStatusStyle = (status) => {
  switch (status) {
    case "Operational":
      return { borderColor: "#BDE9CB", backgroundColor: "#E4F7EA" };
    case "Active incident":
      return { borderColor: "#FFD9A3", backgroundColor: "#FFF3E2" };
    case "Outage":
      return { borderColor: "#FAC6C6", backgroundColor: "#FFF1F1" };
    default:
      return {};
  }
};

function ServiceStatusPage({ serviceKey, serviceId: catalogServiceId }) {
  const service = SERVICE_HISTORY_PAGES[serviceKey]
    || Object.values(SERVICE_HISTORY_PAGES).find(item => item.serviceId === catalogServiceId)
    || { serviceId: catalogServiceId, displayName: "Service" };
  const [resolvedLimit, setResolvedLimit] = useState(50);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [serviceIncidents, setServiceIncidents] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signupMessage, setSignupMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);
    setServiceStatus(null);
    setServiceIncidents([]);
    setResolvedLimit(50);
    setSignupMessage(null);
    if (!Number.isInteger(service.serviceId) || service.serviceId <= 0) {
      setError("This service is unavailable or is no longer monitored.");
      setLoading(false);
      return;
    }

    Promise.all([
      fetch(`${API_URL}/GetServiceStatus/${service.serviceId}`).then((response) => {
        if (!response.ok) {
          throw new Error(`Service status request failed: ${response.statusText}`);
        }
        return response.json();
      }),
      fetch(`${API_URL}/GetServiceIncidents/${service.serviceId}`).then((response) => {
        if (!response.ok) {
          throw new Error(`Service incidents request failed: ${response.statusText}`);
        }
        return response.json();
      }),
    ])
      .then(([status, incidents]) => {
        if (cancelled) {
          return;
        }

        setServiceStatus(status);
        setServiceIncidents(Array.isArray(incidents) ? incidents : []);
      })
      .catch((fetchError) => {
        if (!cancelled) {
          setError("Unable to load this service. It may be unavailable or no longer monitored. Please try again later.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [service.serviceId]);

  const visibleIncidents = useMemo(() => incidentPreview(serviceIncidents, resolvedLimit), [serviceIncidents, resolvedLimit]);
  const resolvedCount = serviceIncidents.filter(incident => incidentState(incident.Status) === "resolved").length;

  const displayName = serviceStatus?.DisplayName || service.displayName;
  const currentStatus = serviceStatus?.CurrentStatus || "Unknown";

  useEffect(() => {
    const originalTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const originalDescription = description?.getAttribute("content");
    document.title = `${displayName} status and incident history | SignalTuner`;
    description?.setAttribute("content", `Current ${displayName} status and full recorded incident history, with optional email alerts.`);
    return () => {
      document.title = originalTitle;
      if (description && originalDescription !== null) description.setAttribute("content", originalDescription);
    };
  }, [displayName]);

  const handleSignup = () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(email)) {
      alert("Please enter a valid email address.");
      return;
    }

    fetch(`${API_URL}/AlertEmailSignup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, serviceId: service.serviceId }),
    })
      .then((response) => {
        if (response.status === 409) {
          alert("This email is already subscribed.");
          throw new Error("Email already subscribed");
        }

        if (!response.ok) {
          throw new Error(`Email signup failed: ${response.statusText}`);
        }

        return response.json();
      })
      .then((data) => {
        setSignupMessage(data.message);
        setEmail("");
      })
      .catch((signupError) => {
        if (signupError.message !== "Email already subscribed") {
          setSignupMessage("An error occurred while signing up. Please try again.");
        }
      });
  };

  return (
    <main className="service-status-page">
      <div className="service-status-shell">
        <section className="service-status-hero-card">
          {service.logo ? <img className="service-status-service-logo" src={`https://www.signaltuner.com/images/serviceicons/${service.logo}.png`} alt={`${displayName} logo`} onError={event => { event.currentTarget.style.visibility = "hidden"; }} /> : <div className="service-status-logo-placeholder" aria-hidden="true">{displayName.slice(0, 1)}</div>}
          <div className="service-status-meta">
            <span>Current status</span>
            <div className="service-status-row">
              <h1>{displayName}</h1>
              <span className="service-status-pill" style={getStatusStyle(currentStatus)}>
                {currentStatus}
              </span>
            </div>
            <p>Current status and full recorded incident history for {displayName}, with optional email alerts when things change.</p>
          </div>

          {loading && <p className="service-status-feedback">Loading...</p>}
          {error && <p role="alert" className="service-status-feedback service-status-error">{error}</p>}
        </section>

        <section className="service-status-signup-card">
          <div className="service-status-signup-copy">
            <h2>Get alerts for incidents and outages</h2>
            <p>Subscribe with your email to hear about new issues affecting {displayName}.</p>
            <div className="service-status-powered-by">
              <span>Powered by</span>
              <img src="https://www.signaltuner.com/images/SignalTUNER%20Logo%20Horizontal-01.png" alt="SignalTuner logo" />
            </div>
          </div>

          <div className="service-status-email-row">
            <input type="email" aria-label="Email for service alerts" disabled={!serviceStatus || !!error} value={email} onChange={(event) => setEmail(event.target.value)} placeholder="Enter your email" />
            <button type="button" onClick={handleSignup} disabled={!serviceStatus || !!error}>
              Submit
            </button>
          </div>

          {signupMessage && <p className="service-status-feedback">{signupMessage}</p>}
        </section>

        <section className="service-status-incidents-card">
          <div className="service-status-section-heading">
            <h2>Full incident history</h2>
            <p>All recorded reports, with active incidents and outages shown first.</p>
          </div>

          {visibleIncidents.length > 0 && (
            <div className="service-status-incidents-list">
              {visibleIncidents.map((incident) => (
                <ExpandableIncident key={incident.IncidentId} incident={incident} serviceName={displayName} />
              ))}
            </div>
          )}

          {serviceIncidents.length > 0 && <div className="service-status-history-footer">
            <p role="status">Showing {visibleIncidents.length} of {serviceIncidents.length} reports</p>
            {resolvedCount > resolvedLimit && <button type="button" onClick={() => setResolvedLimit(limit => limit + 50)}>Show more history</button>}
          </div>}
          {serviceIncidents.length === 0 && !loading && !error && <p className="service-status-empty-state">No incidents reported.</p>}
        </section>
      </div>
    </main>
  );
}

function ExpandableIncident({ incident, serviceName }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded((current) => !current);
  };

  return (
    <article className="service-status-incident" data-incident-state={incidentState(incident.Status)}>
      <button type="button" className="service-status-incident-toggle" onClick={toggleExpanded} aria-expanded={isExpanded}>
        <div>
          <h3>{incident.Title}</h3>
          <p>
            Status
            <span className="service-status-incident-pill" data-incident-state={incidentState(incident.Status)}>
              {incidentLabel(incident.Status)}
            </span>
          </p>
        </div>
        <span className="service-status-expand-icon" data-expanded={isExpanded} aria-hidden="true">
          >
        </span>
      </button>

      {isExpanded && (
        <div className="service-status-incident-content">
          <div className="service-status-description">{incidentText(incident.Description)}</div>
          {incidentReportLink(incident.Link) && <a href={incidentReportLink(incident.Link)} target="_blank" rel="noopener noreferrer">
            {serviceName} incident report
          </a>}
          <p>Published on: {new Date(incident.PublishDate).toLocaleDateString()}</p>
        </div>
      )}
    </article>
  );
}

export const ZoomStatus = () => <ServiceStatusPage serviceKey="zoom" />;
export const TeamsStatus = () => <ServiceStatusPage serviceKey="teams" />;
export const Microsoft365Status = () => <ServiceStatusPage serviceKey="microsoft365" />;

export function ServiceHistoryRoute() {
  const { serviceId } = useParams();
  return <ServiceStatusPage key={serviceId} serviceId={Number(serviceId)} />;
}

export default ServiceStatusPage;
